import React, { useState, useEffect } from "react";
import backend from "../../../../api";
import { Notification, url_encode } from "../../../../utils";
import { useSelector } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";
import { Helmet } from "react-helmet";
import ReactPaginate from "react-paginate";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const AccountHistory = () => {
  const { user } = useSelector((state) => state.auth);
  const [transactions, setTransactions] = useState([]);
  const [userBalance, setUserBalance] = useState({});
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [period, setPeriod] = useState("today");

  const fetchTransactions = (page) => {
    if (user && user.user._id) {
      const status = new Notification();
      setLoading(true);
      backend
        .get("/userwallet", {
          params: {
            user: user.user._id,
            page,
            limit: 5,
            populate: url_encode([
              {
                path: "user",
              },
              {
                path: "order",
                populate: [
                  {
                    path: "items",
                    populate: [
                      {
                        path: "product",
                        populate: [
                          { path: "merchant_user", select: "-password" },
                        ],
                      },
                    ],
                  },
                ],
              },
            ]),
          },
        })
        .then(({ data }) => {
          console.log(data);
          setTransactions(data.data || []);
          setUserBalance(data.earnings || {});
          setTotalPages(data.totalPages || 1);
        })
        .catch((error) => {
          status.set_toast(
            "error",
            error.response ? error.response.data.message : error.message
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const getAnalytics = (selectedPeriod) => {
    if (user && user.user._id) {
      setLoading(true);
      backend
        .get(`/userwallet/analytics/${selectedPeriod}`, {
          params: { userId: user.user._id },
        })
        .then(({ data }) => {
          console.log(data);
          const chartData = data.chartData.map((item) => ({
            name: item.date, // X-axis
            earnings: item.earnings, // Y-axis
          }));
          setAnalytics(chartData);
        })
        .catch((error) => {
          console.error(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    fetchTransactions(page);
    getAnalytics(period);
  }, [user, page, period]);

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
  };

  const handlePageClick = (data) => {
    const selectedPage = data.selected + 1;
    setPage(selectedPage);
  };

  // Determine the X-axis label based on the period
  const formatXAxis = (tickItem) => {
    if (period === "today") {
      return `${tickItem}`;
    } else if (period === "weekly") {
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      return days[tickItem - 1];
    } else if (period === "monthly") {
      return `${tickItem}`;
    } else if (period === "yearly") {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return months[tickItem - 1];
    }
    return tickItem;
  };

  const formatYAxis = (tickItem) => {
    return `$${tickItem}`;
  };

  const formatTooltip = (value) => {
    return `$${value}`;
  };

  return (
    <>
      <Helmet>
        <title>eksplode! | Account History</title>
      </Helmet>
      {loading ? (
        <div className="dashboard">
          <div className="box-account box-info mt-3">
            <div className="my-5 row justify-content-center">
              <ClipLoader color={"#000000"} loading={loading} size={60} />
            </div>
          </div>
        </div>
      ) : user ? (
        <div className="dashboard">
          <div className="box-account box-info">
            <div className="box-head d-flex justify-content-between">
              <h2>Account History</h2>
              <div>
                <select
                  value={period}
                  onChange={handlePeriodChange}
                  className="form-control"
                >
                  <option value="today">Today</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
            <div className="welcome-msg"></div>
            <div className="row mt-3">
              <div className="col-lg-12 mx-auto register-page section-b-space">
                <div className="theme-card">
                  <div className="contact-page">
                    <div className="row">
                      <div className="col-sm-12">
                        <div className="box">
                          <div className="box-title d-flex justify-content-between">
                            <h3>Total Amount Earned</h3>
                            <div className="welcome-msg text-muted">
                              <h4>
                                {userBalance?.net_income?.toFixed(2)} <b>USD</b>
                              </h4>
                            </div>
                          </div>
                          <div className="box-title d-flex justify-content-between">
                            <h3>Total Balance</h3>
                            <div className="welcome-msg text-muted">
                              <h4>
                                {userBalance?.withdrawable?.toFixed(2)}{" "}
                                <b>USD</b>
                              </h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col-sm-12">
                      <div className="box">
                        <div className="box-title d-flex justify-content-between">
                          <h3>Total Earnings ({period})</h3>
                          <div className="welcome-msg text-muted">
                            <h4>
                              {analytics.length > 0
                                ? analytics.reduce(
                                    (acc, cur) => acc + cur.earnings,
                                    0
                                  )
                                : 0}{" "}
                              <b>USD</b>
                            </h4>
                          </div>
                        </div>
                        <div className="box-content py-3">
                          <ResponsiveContainer width="100%" height={300}>
                            <LineChart
                              data={analytics}
                              margin={{
                                top: 5,
                                right: 20,
                                bottom: 5,
                                left: 0,
                              }}
                            >
                              <defs>
                                <linearGradient
                                  id="colorEarnings"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#ff4c3b"
                                    stopOpacity={0.8}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#ff4c3b"
                                    stopOpacity={0}
                                  />
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#ccc"
                              />
                              <XAxis
                                dataKey="name"
                                tickFormatter={formatXAxis}
                              />
                              <YAxis tickFormatter={formatYAxis} />
                              <Tooltip
                                formatter={formatTooltip}
                                contentStyle={{
                                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                                  borderRadius: "10px",
                                }}
                                itemStyle={{ color: "#ff4c3b" }}
                              />
                              <Legend verticalAlign="top" align="right" />
                              <Line
                                type="monotone"
                                dataKey="earnings"
                                stroke="#ff4c3b"
                                fillOpacity={1}
                                fill="url(#colorEarnings)"
                                strokeWidth={2}
                                dot={{
                                  r: 5,
                                  stroke: "#ff4c3b",
                                  strokeWidth: 2,
                                  fill: "white",
                                }}
                                activeDot={{ r: 8 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                  {transactions.length > 0 ? (
                    transactions.map((item) => (
                      <form className="theme-form" key={item._id}>
                        <div className="box-account box-info mt-3">
                          <div className="row">
                            <div className="col-lg-12 px-5">
                              <div className="box">
                                <div className="box-title">
                                  <h3>
                                    <span>
                                      Amount Earned &mdash;{" "}
                                      {item.amount && (
                                        <b>{item.amount.toFixed(2)}</b>
                                      )}
                                    </span>
                                    <br />
                                    <span>
                                      {item.order && (
                                        <span>
                                          Product Promoted &mdash;{" "}
                                          {
                                            item.order.items?.[0]?.product
                                              ?.product_name
                                          }
                                        </span>
                                      )}
                                    </span>
                                    <br />
                                    {item.order && (
                                      <span>
                                        Order Price &mdash;{" "}
                                        {item.order.total_price}
                                      </span>
                                    )}
                                    <br />
                                  </h3>
                                </div>
                                <div className="box-content d-flex justify-content-between">
                                  <small className="text-muted">
                                    Transaction Date &mdash;{" "}
                                    {new Date(
                                      item.transaction_date
                                    ).toLocaleString()}
                                  </small>
                                </div>
                                <div className="box-content">
                                  {item.order && (
                                    <small className="text-muted">
                                      Retailer Name &mdash;{" "}
                                      {item.order.items?.[0]?.product
                                        ?.merchant_user?.first_name +
                                        " " +
                                        item.order.items?.[0]?.product
                                          ?.merchant_user?.last_name}
                                    </small>
                                  )}
                                  <br />
                                  <small className="text-muted">
                                    Program &mdash; {item.payment_source}
                                  </small>
                                </div>
                                <div className="box-content">
                                  <h6 className="mb-2 d-flex justify-content-between">
                                    <small className="text-muted">
                                      Transaction Id &mdash;{" "}
                                      {item.transaction_number}
                                      <br />
                                    </small>
                                  </h6>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </form>
                    ))
                  ) : (
                    <form className="theme-form">
                      <div className="box-account box-info mt-3">
                        <div className="my-5 row justify-content-center">
                          <h3>Not Available</h3>
                        </div>
                      </div>
                    </form>
                  )}
                  <div className="d-flex justify-content-center mt-5">
                    <ReactPaginate
                      previousLabel={"<<"}
                      nextLabel={">>"}
                      breakLabel={"..."}
                      pageCount={totalPages}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={5}
                      onPageChange={handlePageClick}
                      containerClassName={"pagination"}
                      pageClassName={"page-item"}
                      pageLinkClassName={"page-link"}
                      previousClassName={"page-item"}
                      previousLinkClassName={"page-link"}
                      nextClassName={"page-item"}
                      nextLinkClassName={"page-link"}
                      breakClassName={"page-item"}
                      breakLinkClassName={"page-link"}
                      activeClassName={"active"}
                      forcePage={page - 1}
                    />
                  </div>
                  <style jsx>{`
                    .pagination {
                      display: flex;
                      justify-content: center;
                      padding: 1rem;
                      list-style: none;
                    }
                  `}</style>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div></div>
      )}
    </>
  );
};

export default AccountHistory;
